/**
 * PsicoSalud — Funciones de acceso a la API, agrupadas por módulo.
 *
 * Cada grupo corresponde a un módulo del sistema descrito en la ficha
 * técnica del proyecto, de modo que la trazabilidad entre objetivos
 * específicos y código sea directa.
 */

import { request, tokenStore } from './client';
import type {
  AlertEvaluation,
  Appointment,
  AppointmentStatus,
  ClinicalNote,
  ClinicalRecord,
  Consent,
  ConsentType,
  FormResponseResult,
  FormTemplate,
  JoinSession,
  Modality,
  NlpAnalysis,
  Patient,
  PatientPage,
  RecordType,
  RiskLevel,
  TimeSlot,
  TokenPair,
  TrendAnalysis,
  TrendMeasurement,
  User,
  VideoSession,
} from '@/types';

// ============================================================
// Autenticación
// ============================================================
export const authApi = {
  async login(email: string, password: string): Promise<TokenPair> {
    const tokens = await request<TokenPair>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuth: true,
    });
    tokenStore.save(tokens);
    return tokens;
  },

  register(payload: {
    email: string;
    password: string;
    full_name: string;
    license_number?: string;
    specialization?: string;
  }): Promise<User> {
    // El alta de profesionales exige rol de administrador, así que la
    // petición debe llevar el token: no es un registro autoservicio.
    return request<User>('/auth/register', { method: 'POST', body: payload });
  },

  me(): Promise<User> {
    return request<User>('/auth/me');
  },

  changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return request('/auth/change-password', {
      method: 'PUT',
      body: { current_password: currentPassword, new_password: newPassword },
    });
  },

  /** Lista las cuentas del sistema. Reservado al perfil administrativo. */
  listUsers(): Promise<User[]> {
    return request<User[]>('/auth/users');
  },

  /**
   * Da de baja una cuenta y le corta el acceso de inmediato.
   *
   * Es baja lógica: la cuenta se conserva porque sus acciones quedan
   * referenciadas en el historial clínico y en la auditoría.
   */
  deactivateUser(userId: string): Promise<User> {
    return request<User>(`/auth/users/${userId}/deactivate`, { method: 'PUT' });
  },

  activateUser(userId: string): Promise<User> {
    return request<User>(`/auth/users/${userId}/activate`, { method: 'PUT' });
  },

  logout(): void {
    tokenStore.clear();
  },
};

// ============================================================
// Módulo 2 — Pacientes
// ============================================================
export const patientsApi = {
  list(params: { limit?: number; offset?: number; include_inactive?: boolean } = {}): Promise<PatientPage> {
    return request<PatientPage>('/patients/', { query: params });
  },

  get(patientId: string): Promise<Patient> {
    return request<Patient>(`/patients/${patientId}`);
  },

  searchByDocument(documentId: string): Promise<Patient> {
    return request<Patient>('/patients/search', { query: { document_id: documentId } });
  },

  create(payload: {
    document_id: string;
    full_name: string;
    birth_date?: string | null;
    gender?: string | null;
    phone?: string | null;
    email?: string | null;
    emergency_contact?: string | null;
    address?: string | null;
  }): Promise<Patient> {
    return request<Patient>('/patients/', { method: 'POST', body: payload });
  },

  update(patientId: string, payload: Partial<Patient>): Promise<Patient> {
    return request<Patient>(`/patients/${patientId}`, { method: 'PUT', body: payload });
  },

  deactivate(patientId: string): Promise<{ message: string }> {
    return request(`/patients/${patientId}`, { method: 'DELETE' });
  },

  listConsents(patientId: string): Promise<Consent[]> {
    return request<Consent[]>(`/patients/${patientId}/consents`);
  },

  registerConsent(
    patientId: string,
    payload: { consent_type: ConsentType; is_granted: boolean; consent_text?: string; signature?: string },
  ): Promise<Consent> {
    return request<Consent>(`/patients/${patientId}/consents`, { method: 'POST', body: payload });
  },

  exportFhir(patientId: string): Promise<Record<string, unknown>> {
    return request(`/patients/${patientId}/fhir`);
  },
};

// ============================================================
// Módulo 1 — Agenda y citas
// ============================================================
export const appointmentsApi = {
  list(filters: {
    date_from?: string;
    date_to?: string;
    status?: AppointmentStatus;
    modality?: Modality;
    patient_id?: string;
  } = {}): Promise<Appointment[]> {
    return request<Appointment[]>('/appointments/', { query: filters });
  },

  get(appointmentId: string): Promise<Appointment> {
    return request<Appointment>(`/appointments/${appointmentId}`);
  },

  create(payload: {
    patient_id: string;
    start_time: string;
    end_time: string;
    modality: Modality;
    notes?: string;
    recurrence_rule?: string;
  }): Promise<Appointment> {
    return request<Appointment>('/appointments/', { method: 'POST', body: payload });
  },

  update(
    appointmentId: string,
    payload: { start_time?: string; end_time?: string; status?: AppointmentStatus; notes?: string; modality?: Modality },
  ): Promise<Appointment> {
    return request<Appointment>(`/appointments/${appointmentId}`, { method: 'PUT', body: payload });
  },

  cancel(appointmentId: string, reason?: string): Promise<{ message: string }> {
    // El motivo viaja como parámetro de consulta porque así lo declara el
    // endpoint, y se almacena cifrado junto a la cita.
    return request(`/appointments/${appointmentId}`, {
      method: 'DELETE',
      query: { reason: reason?.trim() || undefined },
    });
  },

  availableSlots(psychologistId: string, date: string): Promise<TimeSlot[]> {
    return request<TimeSlot[]>(`/appointments/available-slots/${psychologistId}`, { query: { date } });
  },
};

// ============================================================
// Módulo 2 — Historial clínico
// ============================================================
export const clinicalRecordsApi = {
  create(payload: {
    patient_id: string;
    appointment_id?: string | null;
    record_type: RecordType;
    chief_complaint?: string;
    diagnosis?: string;
    treatment_plan?: string;
    observations?: string;
    icd10_codes?: string[];
    risk_level?: RiskLevel;
    session_date: string;
  }): Promise<ClinicalRecord> {
    return request<ClinicalRecord>('/clinical-records/', { method: 'POST', body: payload });
  },

  get(recordId: string): Promise<ClinicalRecord> {
    return request<ClinicalRecord>(`/clinical-records/${recordId}`);
  },

  patientHistory(patientId: string): Promise<ClinicalRecord[]> {
    return request<ClinicalRecord[]>(`/clinical-records/patient/${patientId}/history`);
  },

  addNote(
    recordId: string,
    payload: { content: string; note_type?: string; ai_analysis_consent?: boolean },
  ): Promise<ClinicalNote & { clinical_record_id: string; author_id: string; ai_analysis_consent: boolean }> {
    return request(`/clinical-records/${recordId}/notes`, { method: 'POST', body: payload });
  },

  analyzeNote(
    recordId: string,
    noteId: string,
  ): Promise<{ message: string; results: NlpAnalysis; disclaimer: string }> {
    return request(`/clinical-records/${recordId}/notes/${noteId}/analyze`, { method: 'POST' });
  },
};

// ============================================================
// Módulo 3 — Sesiones remotas
// ============================================================
export const videoApi = {
  getByAppointment(appointmentId: string): Promise<VideoSession> {
    return request<VideoSession>(`/video-sessions/appointment/${appointmentId}`);
  },

  create(appointmentId: string): Promise<VideoSession> {
    return request<VideoSession>('/video-sessions/', {
      method: 'POST',
      query: { appointment_id: appointmentId },
    });
  },

  join(sessionId: string): Promise<JoinSession> {
    return request<JoinSession>(`/video-sessions/${sessionId}/join`, { method: 'POST' });
  },

  end(sessionId: string): Promise<VideoSession> {
    return request<VideoSession>(`/video-sessions/${sessionId}/end`, { method: 'POST' });
  },
};

// ============================================================
// Módulo 4 — Formularios dinámicos
// ============================================================
export const formsApi = {
  listTemplates(category?: string): Promise<FormTemplate[]> {
    return request<FormTemplate[]>('/forms/templates', { query: { category } });
  },

  getTemplate(templateId: string): Promise<FormTemplate> {
    return request<FormTemplate>(`/forms/templates/${templateId}`);
  },

  startResponse(payload: {
    template_id: string;
    patient_id: string;
    clinical_record_id?: string | null;
  }): Promise<FormResponseResult> {
    return request<FormResponseResult>('/forms/responses', { method: 'POST', body: payload });
  },

  completeResponse(responseId: string, answers: Record<string, number | string>): Promise<FormResponseResult> {
    return request<FormResponseResult>(`/forms/responses/${responseId}/complete`, {
      method: 'PUT',
      body: { answers },
    });
  },

  patientResponses(patientId: string, scaleCode?: string): Promise<FormResponseResult[]> {
    return request<FormResponseResult[]>(`/forms/responses/patient/${patientId}`, {
      query: { scale_code: scaleCode },
    });
  },

  seedDefaults(): Promise<{ message: string; scales: string[] }> {
    return request('/forms/seed-defaults', { method: 'POST' });
  },
};

// ============================================================
// Módulo 5 — Análisis con IA
// ============================================================
// Estos endpoints viven en el microservicio de IA, pero se consumen bajo
// el mismo origen que la interfaz: el servidor de desarrollo los proxea
// desde `/ai` (ver vite.config.ts). Apuntar al puerto 8001 desde el
// navegador solo funciona cuando la aplicación se abre en la misma
// máquina que ejecuta el servicio; con la aplicación publicada, el
// navegador buscaría el localhost del usuario.
//
// En producción se define VITE_AI_SERVICE_URL con la URL del servicio.
// `||` y no `??`: una variable declarada pero vacía —`VITE_AI_SERVICE_URL=`
// en un `.env.local`— pasa el filtro de `??` y deja la base en cadena
// vacía, con lo que las llamadas al servicio de IA terminan en el
// backend, que no tiene esas rutas, y la pantalla de indicadores queda
// muerta con un 404 silencioso.
const AI_BASE = import.meta.env.VITE_AI_SERVICE_URL || '/ai';

async function aiRequest<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${AI_BASE}/api/v1${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`El servicio de análisis respondió ${response.status}`);
  }
  return (await response.json()) as T;
}

export const aiApi = {
  trend(payload: {
    patient_id?: string;
    escala: string;
    mediciones: TrendMeasurement[];
    proyectar_sesiones?: number;
  }): Promise<TrendAnalysis> {
    return aiRequest<TrendAnalysis>('/ml/time-series/trend', payload);
  },

  evaluateAlerts(payload: {
    patient_id: string;
    psychologist_id: string;
    indicadores_riesgo_activos?: string[];
    puntaje_indicadores_riesgo?: number;
    polaridad_actual?: number;
    polaridad_previa?: number | null;
    escalas?: {
      escala: string;
      puntaje_actual: number;
      puntaje_previo?: number | null;
      severidad?: string;
      cambio_confiable?: string;
      requiere_revision_inmediata?: boolean;
      items_criticos?: string[];
    }[];
    citas_perdidas_consecutivas?: number;
    dias_desde_ultima_sesion?: number;
  }): Promise<AlertEvaluation> {
    return aiRequest<AlertEvaluation>('/alerts/evaluate', payload);
  },
};
